import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  applySetup,
  createPreviewLink,
  onlineStorePreferencesDeepLink,
  themeEmbedDeepLink,
  type MarketsPayload,
  type SafegeoStatus,
} from "../lib/safegeo-api";
import {
  countryLabel,
  destinationLabel,
  isFreePlan,
  planLabel,
} from "../lib/format";
import { SafePage } from "../components/SafePage";
import { PageIntro } from "../components/PageIntro";
import { AppButton } from "../components/AppButton";
import {
  ErrorBanner,
  InfoBanner,
  WarningBanner,
} from "../components/PageBanner";

const API_KEY = import.meta.env.VITE_SHOPIFY_API_KEY || "";

type Props = {
  status: SafegeoStatus;
  markets: MarketsPayload;
  embedOn: boolean;
  checking: boolean;
  onSyncEmbed: () => void;
  onRefresh: () => void;
  /** Optional - return to merchant Home shell without finishing every step */
  onBackToHome?: () => void;
};

/** Final P0 #5 - one checklist, 3 steps, same order everywhere. */
type StepId = "embed" | "markets" | "preview";

/**
 * First-run onboarding (Home replaces this when embed + markets publish are done).
 * @see SafeGeo_UI_UX_Final_v1.4 - Finish setup 1·2·3
 */
export function SetupPanel({
  status,
  markets,
  embedOn,
  checking,
  onSyncEmbed,
  onRefresh,
  onBackToHome,
}: Props) {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<"market" | "multi" | "block">("market");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManualEmbed, setShowManualEmbed] = useState(false);
  const [prefsChecked, setPrefsChecked] = useState(() => {
    try {
      return localStorage.getItem("sg_prefs_checked") === "1";
    } catch {
      return false;
    }
  });
  const step2Done = !!status.settings?.setup_step2_at;
  const storeTested = !!status.last_tested_at;

  const free = isFreePlan(status.plan, status.plan_key);
  const plan = planLabel(status.plan || status.plan_key);
  const canBlock = !free;
  const canMulti = plan === "Multi-store" || plan === "Scale";
  const shopifyMarkets = markets.countries || [];
  const marketCountries =
    (status.settings?.market_countries?.length
      ? status.settings.market_countries
      : shopifyMarkets) || [];
  const hasMultiMarkets = marketCountries.length > 1;
  const currencies = markets.currencies || {};
  const primaryCur = marketCountries[0]
    ? currencies[marketCountries[0]] || ""
    : "";
  const marketRows = hasMultiMarkets
    ? marketCountries.map((code, i) => ({
        code,
        label: (markets.labels[i] || countryLabel(code)).replace(
          /\s*\([A-Z]{3}\)\s*$/,
          "",
        ),
        where: destinationLabel(code, currencies[code]),
        currency: currencies[code] || "",
      }))
    : [];

  const shop = status.shop_domain || "";
  const embedEditorUrl =
    shop && API_KEY ? themeEmbedDeepLink(shop, API_KEY) : null;
  const previewCc =
    marketCountries.length > 1
      ? marketCountries[1]
      : marketCountries[0] || "CA";
  const preferencesUrl = shop
    ? onlineStorePreferencesDeepLink(shop)
    : null;

  const step3Ready = embedOn && step2Done;
  const completedCount =
    (embedOn ? 1 : 0) + (step2Done ? 1 : 0) + (storeTested ? 1 : 0);

  const defaultExpanded: StepId = !embedOn
    ? "embed"
    : !step2Done
      ? "markets"
      : "preview";

  const [expanded, setExpanded] = useState<Partial<Record<StepId, boolean>>>({
    [defaultExpanded]: true,
  });

  useEffect(() => {
    setExpanded({ [defaultExpanded]: true });
  }, [defaultExpanded]);

  const steps = useMemo(
    () =>
      [
        {
          id: "embed" as const,
          label: "Turn on SafeGeo in your theme",
          done: embedOn,
          waiting: false,
        },
        {
          id: "markets" as const,
          label: "Check where visitors go",
          done: step2Done,
          waiting: !embedOn,
        },
        {
          id: "preview" as const,
          label: "Test your store",
          done: storeTested,
          waiting: !step3Ready,
        },
      ] as const,
    [embedOn, step2Done, step3Ready, storeTested],
  );

  function markPrefsChecked() {
    setPrefsChecked(true);
    try {
      localStorage.setItem("sg_prefs_checked", "1");
    } catch {
      /* ignore */
    }
  }

  function toggle(id: StepId) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  async function saveSetup() {
    setBusy(true);
    setError(null);
    try {
      let nextPreset = free ? "market" : preset;
      if (nextPreset === "block" && !canBlock) nextPreset = "market";
      if (nextPreset === "multi" && !canMulti) nextPreset = "market";
      const result = await applySetup({
        preset: nextPreset,
        market_countries: marketCountries,
        markets_count: Math.max(1, markets.count || marketCountries.length),
      });
      onRefresh();
      if (result.next === "stores") navigate("/app/stores");
      else if (result.next === "block_rule")
        navigate("/app/block/new");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function openPreview() {
    setBusy(true);
    setError(null);
    try {
      const link = await createPreviewLink(previewCc);
      window.open(link.url, "_blank", "noopener,noreferrer");
      onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SafePage screen="setup" heading="SafeGeo">
      <PageIntro>
        Help shoppers find the right version of your store. You’re 3 steps from
        live - turn SafeGeo on, check where visitors go, then preview as a
        visitor from another country.
      </PageIntro>
      {onBackToHome ? (
        <s-box paddingBlockEnd="base">
          <s-button variant="tertiary" onClick={onBackToHome}>
            Back to Home
          </s-button>
        </s-box>
      ) : null}
      {error ? (
        <ErrorBanner heading="Couldn't save setup">{error}</ErrorBanner>
      ) : null}

      <s-section
        heading="Finish setup"
        subheading="Same 3 steps as Home - with the Markets table here."
      >
        <s-text slot="supplemental" color="subdued">
          {completedCount} of {steps.length} steps completed
          {completedCount >= 2 ? " · Almost done" : ""}
        </s-text>

        <s-box paddingBlockEnd="base">
          <s-progress
            value={completedCount}
            max={steps.length}
            accessibilityLabel={`${completedCount} of ${steps.length} setup steps completed`}
          />
        </s-box>

        <s-stack gap="small">
          {steps.map((step, index) => {
            const open = !!expanded[step.id];
            return (
              <s-stack key={step.id} gap="small">
                {index > 0 ? <s-divider /> : null}
                <s-grid
                  gridTemplateColumns="1fr auto"
                  gap="base"
                  alignItems="center"
                >
                  <s-stack direction="inline" gap="small" alignItems="center">
                    <s-badge
                      tone={
                        step.done
                          ? "success"
                          : step.waiting
                            ? "info"
                            : "attention"
                      }
                    >
                      {step.done
                        ? "Complete"
                        : step.waiting
                          ? "Waiting"
                          : "To do"}
                    </s-badge>
                    <s-text type="strong">{step.label}</s-text>
                    {step.id === "embed" && checking ? (
                      <s-text color="subdued">Checking…</s-text>
                    ) : null}
                  </s-stack>
                  <s-button
                    type="button"
                    variant="tertiary"
                    tone="neutral"
                    icon={open ? "chevron-up" : "chevron-down"}
                    accessibilityLabel={
                      open
                        ? `Hide ${step.label} details`
                        : `Show ${step.label} details`
                    }
                    onClick={() => toggle(step.id)}
                  />
                </s-grid>

                {open && step.id === "embed" ? (
                  <s-stack gap="small" paddingInlineStart="large">
                    <s-paragraph color="subdued">
                      {embedOn
                        ? "App embed is on. You can reopen the theme editor anytime."
                        : "Opens the theme editor with SafeGeo ready. Click Save, then return here - we mark this complete automatically."}
                    </s-paragraph>
                    <s-stack direction="inline" gap="small">
                      {embedEditorUrl ? (
                        <s-button
                          variant={embedOn ? "secondary" : "primary"}
                          href={embedEditorUrl}
                          target="_blank"
                          onClick={() => {
                            void onSyncEmbed();
                          }}
                        >
                          {embedOn ? "Manage in theme" : "Turn on in theme"}
                        </s-button>
                      ) : (
                        <s-button
                          variant="primary"
                          onClick={() => setShowManualEmbed(true)}
                        >
                          Turn on in theme
                        </s-button>
                      )}
                      {embedEditorUrl ? (
                        <s-button
                          type="button"
                          variant="tertiary"
                          tone="neutral"
                          onClick={() => setShowManualEmbed((v) => !v)}
                        >
                          {showManualEmbed ? "Hide how-to" : "Show me how"}
                        </s-button>
                      ) : null}
                    </s-stack>
                    {showManualEmbed || !embedEditorUrl ? (
                      <s-paragraph color="subdued">
                        Online Store → Themes → Customize → App embeds →
                        SafeGeo → Save.
                      </s-paragraph>
                    ) : null}
                  </s-stack>
                ) : null}

                {open && step.id === "markets" ? (
                  <s-stack gap="small" paddingInlineStart="large">
                    {!hasMultiMarkets ? (
                      <WarningBanner heading="Add a second Shopify Market">
                        SafeGeo asks shoppers when this store has{" "}
                        <s-text type="strong">2 or more</s-text> Markets
                        (e.g. United States + Canada). In Shopify admin: Settings
                        → Markets → add a market, then refresh this page.
                        {free
                          ? " On Free, Markets is how routing works - country→URL rules need Starter."
                          : " Or add a country→website rule after setup (Starter+)."}
                      </WarningBanner>
                    ) : (
                      <>
                        <s-paragraph color="subdued">
                          From your Shopify Markets. Shoppers are asked first
                          with a small confirmation (change Ask / Auto later under
                          Where they go).
                        </s-paragraph>
                        <s-table>
                          <s-table-header-row>
                            <s-table-header listSlot="primary">
                              Visitors from
                            </s-table-header>
                            <s-table-header>Where they go</s-table-header>
                          </s-table-header-row>
                          <s-table-body>
                            {marketRows.map((row) => {
                              const sameCurrency =
                                !!row.currency &&
                                !!primaryCur &&
                                row.currency === primaryCur &&
                                row.code !== marketCountries[0];
                              return (
                                <s-table-row key={row.code}>
                                  <s-table-cell>{row.label}</s-table-cell>
                                  <s-table-cell>
                                    {row.where}
                                    {sameCurrency ? (
                                      <>
                                        <br />
                                        <s-text color="subdued">
                                          May still see {primaryCur} prices —
                                          turn on local currencies in Markets.
                                        </s-text>
                                      </>
                                    ) : null}
                                  </s-table-cell>
                                </s-table-row>
                              );
                            })}
                            <s-table-row>
                              <s-table-cell>Everyone else</s-table-cell>
                              <s-table-cell>Stay on this store</s-table-cell>
                            </s-table-row>
                          </s-table-body>
                        </s-table>
                      </>
                    )}

                    <s-box padding="base" borderWidth="base" borderRadius="base">
                      <s-stack direction="block" gap="small">
                        <s-text type="strong">
                          Let SafeGeo ask first instead
                        </s-text>
                        <s-paragraph color="subdued">
                          Turn off Shopify’s Automatic redirection (Online Store
                          → Preferences). SafeGeo asks first and remembers Stay
                          / Switch - Shopify’s setting can’t do that.
                        </s-paragraph>
                        <s-stack direction="inline" gap="small">
                          {preferencesUrl ? (
                            <s-button
                              variant="secondary"
                              href={preferencesUrl}
                              target="_blank"
                            >
                              Open Preferences
                            </s-button>
                          ) : null}
                          <s-button
                            type="button"
                            variant={prefsChecked ? "tertiary" : "secondary"}
                            onClick={() => markPrefsChecked()}
                          >
                            {prefsChecked
                              ? "Marked done"
                              : "I turned it off"}
                          </s-button>
                        </s-stack>
                      </s-stack>
                    </s-box>

                    {!free && (canMulti || canBlock) ? (
                      <s-stack direction="block" gap="small">
                        {canMulti ? (
                          <s-checkbox
                            label="Also connect my other Shopify stores (next)"
                            checked={preset === "multi"}
                            onChange={() =>
                              setPreset((p) =>
                                p === "multi" ? "market" : "multi",
                              )
                            }
                          />
                        ) : null}
                        {canBlock ? (
                          <s-checkbox
                            label="Also set up a country block after this"
                            checked={preset === "block"}
                            onChange={() =>
                              setPreset((p) =>
                                p === "block" ? "market" : "block",
                              )
                            }
                          />
                        ) : null}
                      </s-stack>
                    ) : null}
                    <s-stack direction="inline" gap="small">
                      <s-button
                        variant="primary"
                        disabled={busy || !embedOn}
                        loading={busy}
                        onClick={() => void saveSetup()}
                      >
                        {step2Done
                          ? "Save again"
                          : hasMultiMarkets
                            ? "Save & publish"
                            : "Save for now"}
                      </s-button>
                      {!free ? (
                        <AppButton
                          variant="tertiary"
                          tone="neutral"
                          to="/app/stores/new"
                        >
                          Add a country rule
                        </AppButton>
                      ) : (
                        <AppButton
                          variant="tertiary"
                          tone="neutral"
                          to="/app/plans"
                        >
                          Need another website?
                        </AppButton>
                      )}
                    </s-stack>
                    {!embedOn ? (
                      <s-paragraph color="subdued">
                        Finish the theme step first so we can publish.
                      </s-paragraph>
                    ) : null}
                  </s-stack>
                ) : null}

                {open && step.id === "preview" ? (
                  <s-stack gap="small" paddingInlineStart="large">
                    {!hasMultiMarkets ? (
                      <InfoBanner heading="Preview needs 2+ markets">
                        With one market, SafeGeo has nothing to ask. Add Canada
                        (or another country) in Shopify Markets, save step 2,
                        then preview as that country.
                      </InfoBanner>
                    ) : (
                      <s-paragraph color="subdued">
                        Opens your storefront as a shopper from{" "}
                        {countryLabel(previewCc)}. You should see the
                        confirmation. Doesn’t count toward your plan.
                      </s-paragraph>
                    )}
                    <s-stack direction="inline" gap="small">
                      {hasMultiMarkets ? (
                        <s-button
                          variant={step3Ready ? "primary" : "secondary"}
                          disabled={!step3Ready || busy}
                          loading={busy}
                          onClick={() => void openPreview()}
                        >
                          Preview as {countryLabel(previewCc)}
                        </s-button>
                      ) : null}
                      <AppButton
                        variant="tertiary"
                        tone="neutral"
                        to="/app/markets"
                      >
                        Open Where they go
                      </AppButton>
                    </s-stack>
                  </s-stack>
                ) : null}
              </s-stack>
            );
          })}
        </s-stack>
      </s-section>
    </SafePage>
  );
}
